import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, MapPin, Wallet, Clock, RefreshCw, AlertTriangle, FileDown, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money, errMsg, downloadBlob } from "@/lib/api";
import { LenderLetterButton } from "@/components/LenderLetterButton";
import { CoverLetterButton } from "@/components/CoverLetterButton";
import { LenderLetterLog } from "@/components/EmailLenderLetter";
import { LenderTimeline } from "@/components/LenderOutreach";
import { useAuth } from "@/context/AuthContext";
import { useClients } from "@/components/ClientSelect";
import { CoverPage } from "@/components/CoverPage";
import { LiveSiteData } from "@/components/LiveSiteData";
import { PropertyPhotos } from "@/components/PropertyPhotos";
import { StudyMeta } from "@/components/StudyMeta";
import { RevisionsPanel } from "@/components/RevisionsPanel";
import { StudyFeedback, RatingRequestButton } from "@/components/Feedback";
import { SECTIONS } from "@/components/sectionConfig";
import { SectionCard } from "@/components/SectionCard";
import { FddVerified } from "@/components/FddVerified";
import { RegenerateDialog } from "@/components/RegenerateDialog";
import { GeneratingLoader } from "@/components/GeneratingLoader";
import { ScoreRing, VerdictBadge } from "@/components/ScoreRing";
import { SiteMap } from "@/components/SiteMap";
import { ImproveScore } from "@/components/ImproveScore";
import { SharePreview } from "@/components/SharePreview";
import { EmailPdf } from "@/components/EmailPdf";
import { DraftWatermark, ImprovementReport } from "@/components/PrintExtras";

export default function StudyEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [study, setStudy] = useState(null);
  const [regen, setRegen] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [active, setActive] = useState(SECTIONS[0].key);
  const [busy, setBusy] = useState("");
  const { settings, user } = useAuth();
  const clients = useClients();

  const load = useCallback(() => api.get(`/studies/${id}`).then((r) => setStudy(r.data)).catch(() => { toast.error("Study not found"); navigate("/studies"); }), [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (study?.status !== "generating") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [study?.status, load]);

  useEffect(() => {
    if (study?.status !== "ready") return;
    const obs = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && setActive(e.target.id)), { rootMargin: "-30% 0px -60% 0px" });
    SECTIONS.forEach((s) => { const el = document.getElementById(s.key); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [study?.status]);

  if (!study) return <div className="min-h-screen" />;
  if (study.status === "generating") return <GeneratingLoader name={study.input.project_name || study.input.address || "your study"} />;
  if (study.status === "failed") {
    return (
      <div className="max-w-xl mx-auto py-24 px-4" data-testid="generation-failed">
        <AlertTriangle className="h-8 w-8 text-rose-400 mb-4" />
        <h1 className="font-display text-3xl font-bold mb-2">Generation failed</h1>
        <p className="text-slate-400 mb-6">{study.error}</p>
        <Button data-testid="retry-generation-btn" className="bg-emerald-500 text-emerald-950" onClick={() => api.post(`/studies/${id}/retry`).then((r) => setStudy(r.data))}><RefreshCw className="h-4 w-4 mr-2" />Try again</Button>
      </div>
    );
  }

  const currency = study.input.currency || "USD";
  const sections = SECTIONS.filter((s) => !s.optional || study.sections?.[s.key]);
  const saveSection = async (key, data) => {
    try {
      const r = await api.put(`/studies/${id}`, { sections: { ...study.sections, [key]: data } });
      setStudy(r.data);
      toast.success("Section saved");
      return true;
    } catch {
      toast.error("Could not save section");
      return false;
    }
  };
  const regenerate = async ({ tone, instructions }) => {
    setRegenLoading(true);
    try {
      const r = await api.post(`/studies/${id}/sections/${regen.key}/regenerate`, { tone, instructions });
      setStudy((s) => ({ ...s, sections: { ...s.sections, [regen.key]: r.data.section } }));
      toast.success(`${regen.title} rewritten`);
      setRegen(null);
    } catch {
      toast.error("AI rewrite failed, please try again");
    } finally {
      setRegenLoading(false);
    }
  };
  const printPage = () => setTimeout(() => window.print(), 300);
  const exportPdf = async () => {
    setBusy("pdf");
    try { await downloadBlob(`/studies/${id}/export/pdf`, `${(study.input.project_name || "feasibility-study").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/gi, "-")}.pdf`); toast.success("PDF downloaded"); }
    catch { toast.error("PDF export failed"); } finally { setBusy(""); }
  };
  const exportWord = async () => {
    setBusy("word");
    try {
      const r = await api.get(`/studies/${id}/export/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(study.input.project_name || "feasibility-study").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/gi, "-")}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Word document downloaded");
    } catch { toast.error("Word export failed"); } finally { setBusy(""); }
  };
  const refreshSite = async () => {
    setBusy("site");
    try { const r = await api.post(`/studies/${id}/site-data/refresh`); setStudy(r.data); toast.success("Live data & photos refreshed"); }
    catch (e) { toast.error(errMsg(e, "Could not refresh live data")); } finally { setBusy(""); }
  };
  const client = clients.find((c) => c.id === study.client_id);
  const inp = study.input;

  return (
    <div className="print-light">
      <CoverPage study={study} client={client} settings={settings} />
      <ImprovementReport imp={study.last_improvement} />
      <DraftWatermark show={!study.paid} />
      <div className="border-b border-slate-800 bg-slate-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="flex items-center gap-6">
            <ScoreRing score={study.score} size={88} testId="study-score" />
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{inp.industry || "Feasibility study"}</span>
                <VerdictBadge verdict={study.verdict} testId="study-verdict" />
              </div>
              <h1 data-testid="study-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{inp.project_name}</h1>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                {inp.address && <span data-testid="study-address" className="flex items-center gap-1 text-cyan-300"><MapPin className="h-4 w-4" />{inp.address}</span>}
                {inp.location && !inp.address && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{inp.location}</span>}
                {inp.budget && <span className="flex items-center gap-1 num"><Wallet className="h-4 w-4" />{money(inp.budget, currency)}</span>}
                {inp.timeline_months && <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{inp.timeline_months} months</span>}
              </div>
            </div>
          </div>
          <div className="no-print flex flex-wrap gap-3">
            <ImproveScore study={study} onChange={setStudy} autoOpen={params.get("improve") === "1"} />
            <Button data-testid="export-pdf-button" disabled={busy === "pdf"} onClick={exportPdf} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF</Button>
            <Button data-testid="export-word-button" variant="outline" disabled={busy === "word"} onClick={exportWord} className="rounded-full border-slate-700 bg-transparent">
              {busy === "word" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Download Word
            </Button>
            {study.status === "ready" && <LenderLetterButton path={`/studies/${id}/lender-letter`} name={study.input.project_name} />}
            {study.status === "ready" && <CoverLetterButton studyId={id} enabled={!!study.cover_letter?.enabled} onSaved={(cl) => setStudy((s) => ({ ...s, cover_letter: cl }))} />}
            <Button data-testid="print-button" variant="ghost" onClick={printPage} className="rounded-full"><Printer className="h-4 w-4 mr-2" />Print</Button>
          </div>
        </div>
      </div>

      <div className="print-grid-block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="no-print hidden lg:block lg:col-span-3">
          <nav className="sticky top-24 space-y-1" data-testid="section-nav">
            {sections.map((s, i) => (
              <a key={s.key} href={`#${s.key}`} data-testid={`nav-section-${s.key}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${active === s.key ? "bg-emerald-500/10 text-emerald-300" : "text-slate-400 hover:text-slate-100"}`}>
                <span className="num text-xs opacity-60">{String(i + 1).padStart(2, "0")}</span>{s.title}
              </a>
            ))}
          </nav>
        </aside>
        <div className="lg:col-span-9 space-y-8 min-w-0">
          <StudyMeta study={study} clients={clients} onUpdated={setStudy} />
          <LenderLetterLog studyId={id} />
          <LenderTimeline study={study} />
          <StudyFeedback studyId={id} />
          <RatingRequestButton study={study} />
          <SharePreview study={study} onChange={setStudy} />
          {!user?.impersonator && <EmailPdf study={study} client={client} />}
          <RevisionsPanel studyId={id} study={study} />
          <div className="no-print lg:hidden -mx-4 px-4 flex gap-2 overflow-x-auto pb-2" data-testid="mobile-section-nav">
            {sections.map((s) => <a key={s.key} href={`#${s.key}`} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${active === s.key ? "border-emerald-500 text-emerald-300" : "border-slate-700 text-slate-400"}`}>{s.title}</a>)}
          </div>
          {sections.map((cfg, i) => (
            <SectionCard key={cfg.key} cfg={cfg} index={i} data={study.sections?.[cfg.key]} currency={currency}
              onSave={saveSection} onRegenerate={setRegen} regenerating={regenLoading && regen?.key === cfg.key}>
              {cfg.key === "franchise_economics" && <FddVerified fdd={inp.fdd} />}
              {cfg.key === "site" && (
                <>
                  <PropertyPhotos studyId={id} photos={study.site_data?.photos} version={study.site_data?.fetched_at || ""} coverIdx={study.cover_photo}
                    onSetCover={(i) => api.put(`/studies/${id}`, { cover_photo: i }).then((r) => { setStudy(r.data); toast.success("Cover photo updated"); }).catch((e) => toast.error(errMsg(e)))} />
                  <div className="mb-6"><SiteMap address={inp.address} /></div>
                  <LiveSiteData data={study.site_data} />
                  <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
                    <p className="text-xs text-slate-500">{study.site_data?.sources?.length ? "Site scores are based on the live data above; verify zoning and rents locally." : "Site analysis is AI-estimated — refresh to pull live data and photos."}</p>
                    <Button size="sm" variant="outline" data-testid="refresh-site-data-btn" disabled={busy === "site"} onClick={refreshSite} className="border-slate-700 bg-transparent">
                      {busy === "site" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}Refresh live data & photos
                    </Button>
                  </div>
                </>
              )}
            </SectionCard>
          ))}
        </div>
      </div>
      <RegenerateDialog key={regen?.key || "none"} open={!!regen} onOpenChange={(o) => !o && !regenLoading && setRegen(null)} title={regen?.title} onSubmit={regenerate} loading={regenLoading} />
    </div>
  );
}
